import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import dayjs from 'dayjs';
import { Observable, iif, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { BlockAppointment, CartItem, CartMode, CartService } from 'src/api';
import { ItemTreeHierarchyExtended } from 'src/app/models/item-tree';
import { ItemTreeService } from 'src/app/services/item-tree.service';
import * as TreeActions from '../object-tree-store/actions';
import { selectTree } from '../object-tree-store/selects';
import { RootState } from '../root-state.model';
import { NavigateTo } from '../router/actions';
import {
  selectOrderId,
  selectOrderIdQueryString,
  selectOrderIdStatusPage,
} from '../router/selectors';
import { SetSchedulerErrors } from '../scheduler-store/actions';
import { SetTimePickerErrors, SetTimePickerValues } from '../time-picker-store/actions';
import * as CartActions from './actions';

@Injectable()
export class CartEffects {
  private readonly actions$ = inject(Actions);
  private readonly cartService = inject(CartService);
  private readonly itemTreeService = inject(ItemTreeService);
  private readonly store = inject<Store<RootState>>(Store);

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LoadCart),
      withLatestFrom(
        this.store.select(selectOrderIdQueryString),
        this.store.select(selectOrderIdStatusPage),
      ),
      switchMap(([action, orderIdQueryString, orderIdStatusPage]) => {
        const cartMode = orderIdQueryString
          ? CartMode.Admin
          : orderIdStatusPage
            ? CartMode.Status
            : CartMode.Default;

        return this.cartService.getCart(cartMode, action.orderId).pipe(
          map((cart) =>
            CartActions.LoadCartSuccess({
              cart: {
                ...cart,
                allowAddEditItems:
                  cart.items.length === 0 ||
                  cart.items.some(
                    (i) => i.allowEdit || i.allowDelete || i.permissionChangeReservation,
                  ),
              },
            }),
          ),
          catchError((error) => of(CartActions.LoadCartFailure({ error }))),
        );
      }),
    ),
  );

  loadOrderCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LoadOrder),
      withLatestFrom(
        this.store.select(selectOrderIdQueryString),
        this.store.select(selectOrderIdStatusPage),
      ),
      switchMap(([, orderIdQueryString, orderIdStatusPage]) => {
        const cartMode = orderIdQueryString
          ? CartMode.Admin
          : orderIdStatusPage
            ? CartMode.Status
            : CartMode.Default;

        return this.cartService
          .getOrder(cartMode)
          .pipe(map((order) => CartActions.LoadOrderSuccess({ order })));
      }),
    ),
  );

  editReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.EditReservation),
      tap((editAction) => {
        if (editAction.cartItem == null) {
          // unset all errors if edit-mode was left
          this.store.dispatch(TreeActions.SetTreeErrors({ errors: null }));
          this.store.dispatch(SetTimePickerErrors({ errors: null }));
          this.store.dispatch(SetSchedulerErrors({ errors: [] }));
        }
      }),
      withLatestFrom(this.store.select(selectTree)),
      // check whether Tree is loaded. If not: load it and return it.
      switchMap(([editAction, tree]) => {
        return iif(
          () => tree !== null,
          // use current value
          of([editAction, tree]),
          // use Tree from backend
          this.itemTreeService.getItemTree().pipe(
            tap((items) => this.store.dispatch(TreeActions.SetTreeItems({ items }))),
            map((treeAndItems) => [editAction, treeAndItems.tree]),
          ),
        ) as Observable<
          [{ cartItem: CartItem | BlockAppointment | null }, ItemTreeHierarchyExtended[]]
        >;
      }),
      switchMap(([editAction]) => {
        if (!editAction.cartItem) {
          return [];
        }

        const start =
          'start' in editAction.cartItem
            ? dayjs(editAction.cartItem.start)
            : dayjs(editAction.cartItem.startTimeRaw);
        const end =
          'end' in editAction.cartItem
            ? dayjs(editAction.cartItem.end)
            : editAction.cartItem.endTimeRaw
              ? dayjs(editAction.cartItem.endTimeRaw)
              : null;
        const availabilityPlanId =
          'availabilityPlanId' in editAction.cartItem
            ? editAction.cartItem.availabilityPlanId
            : undefined;
        const preparationMinutes =
          'preparationMinutes' in editAction.cartItem
            ? editAction.cartItem.preparationMinutes
            : undefined;
        const followUpMinutes =
          'followUpMinutes' in editAction.cartItem
            ? editAction.cartItem.followUpMinutes
            : undefined;

        return [
          // update all common fields if in edit-mode or edit-mode exited
          SetTimePickerValues({
            start,
            end,
            availabilityPlanId,
            recurrenceFreq: null,
            quantity: 'quantity' in editAction.cartItem ? editAction.cartItem.quantity : null,
            preparationMinutes: preparationMinutes ?? undefined,
            followUpMinutes: followUpMinutes ?? undefined,
          }),
          TreeActions.SetSelectedItemIds({ items: [editAction.cartItem.itemId!] }),
        ];
      }),
    ),
  );

  copyReservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CopyReservation),
      withLatestFrom(this.store.select(selectOrderId)),
      switchMap(([editAction, orderId]) => [
        SetTimePickerValues({
          start: dayjs(editAction.cartItem.startTimeRaw),
          end: editAction.cartItem.endTimeRaw ? dayjs(editAction.cartItem.endTimeRaw) : null,
          availabilityPlanId: editAction.cartItem.availabilityPlanId,
          recurrenceFreq: null,
          quantity: editAction.cartItem.quantity,
        }),
        TreeActions.SetSelectedItemIds({ items: [editAction.cartItem.itemId!] }),
        NavigateTo({ route: 'Home', queryParams: { OrderId: orderId } }),
      ]),
    ),
  );

  selectItemInObjectChoice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SelectItemInObjectChoice),
      withLatestFrom(this.store.select(selectOrderId)),
      switchMap(([editAction, orderId]) => [
        TreeActions.SetSelectedItemIds({ items: [editAction.itemId] }),
        NavigateTo({ route: 'Home', queryParams: { OrderId: orderId } }),
      ]),
    ),
  );
}
